import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiMail, FiUser, FiEdit3, FiSend, FiPaperclip, FiX, FiFile, FiCornerUpRight, FiCornerUpLeft, FiChevronRight, FiFilter, FiInbox, FiRefreshCw, FiCode, FiLayout, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { emailsAPI, emailTemplatesAPI, authAPI, notificationsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canUserSendFromMailbox } from '../../utils/mailboxPermission';
import EmailBodyEditor, { isEmailBodyEmpty } from '../../components/Common/EmailBodyEditor';
import EmailLogAttachments from '../../components/Common/EmailLogAttachments';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const formatDateTime = (v) => {
  if (!v) return '-';
  return new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

/**
 * Detects "rich" HTML that ReactQuill can't faithfully render (tables,
 * <style> blocks, full <html>/<head> docs, or heavy inline styles).
 *
 * When this returns true for a template body, we show the raw HTML inside a
 * sandboxed <iframe> preview instead of feeding it into Quill (Quill would
 * strip <table>, <style>, inline styles, etc., and the user would see the
 * mangled text the screenshot showed).
 */
const isRichEmailHtml = (html) => {
  if (!html || typeof html !== 'string') return false;
  const s = html.toLowerCase();
  if (/<!doctype/.test(s)) return true;
  if (/<html[\s>]/.test(s)) return true;
  if (/<head[\s>]/.test(s)) return true;
  if (/<body[\s>]/.test(s)) return true;
  if (/<style[\s>]/.test(s)) return true;
  if (/<table[\s>]/.test(s)) return true;
  if (/<tr[\s>]/.test(s)) return true;
  if (/<td[\s>]/.test(s)) return true;
  // Many inline styles strongly suggest a designed HTML email
  const styleHits = (s.match(/style\s*=/g) || []).length;
  if (styleHits >= 3) return true;
  return false;
};

/**
 * Renders an email message's HTML body inside a sandboxed, auto-resizing
 * iframe — the same technique Gmail/Outlook use.
 *
 * Why this exists:
 *   The previous implementation used `dangerouslySetInnerHTML` on a `<div>`,
 *   which meant:
 *     1. The email's `<style>` blocks leaked into the CRM's own CSS.
 *     2. The CRM's theme colors/fonts leaked into the email, so designed
 *        HTML templates (tables, colored banners, inline styles) rendered
 *        half-squashed / off-color.
 *     3. Fixed-width tables (width="600") couldn't scroll and got clipped.
 *
 *   By isolating each message in an iframe, the HTML renders EXACTLY as the
 *   recipient will see it in their own inbox.
 *
 * Implementation notes:
 *   - `sandbox="allow-same-origin"` is required so we can read the rendered
 *     document's scrollHeight to auto-size the iframe. We deliberately do
 *     NOT include `allow-scripts`, so <script> tags inside email HTML stay
 *     disabled (XSS-safe).
 *   - A small CSS reset + `overflow-x:auto` is injected so wide tables can
 *     scroll horizontally instead of being clipped.
 *   - Height is polled a few times after content is written to catch
 *     reflows (e.g. when images load).
 */
const MessageBodyFrame = React.memo(function MessageBodyFrame({ html, emptyText }) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(80);

  const safeHtml = html && html.trim() ? html : `<em style="color:#94a3b8">${emptyText || ''}</em>`;
  const srcDoc = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; width: 100%; }
  body {
    padding: 14px 16px;
    font-family: -apple-system, "Segoe UI", Roboto, Arial, Helvetica, sans-serif;
    font-size: 14px;
    line-height: 1.55;
    color: #0f172a;
    background: #ffffff;
    box-sizing: border-box;
    overflow-x: auto;
    word-wrap: break-word;
  }
  * { box-sizing: border-box; }
  img { max-width: 100%; height: auto; }
  /* Tables in email HTML often use fixed widths; let them keep their width
     but allow the page to scroll horizontally instead of clipping. */
  table { max-width: 100%; }
  a { color: #2563eb; }
  blockquote {
    margin: 0 0 10px 0;
    padding: 6px 12px;
    border-left: 3px solid #e2e8f0;
    color: #475569;
  }
</style>
</head>
<body>${safeHtml}</body>
</html>`;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const measure = () => {
      try {
        const doc = frame.contentDocument;
        if (!doc || !doc.body) return;
        // Use the larger of documentElement.scrollHeight / body.scrollHeight
        // to handle both margin-collapse and overflow cases.
        const next = Math.max(
          doc.body.scrollHeight,
          doc.documentElement ? doc.documentElement.scrollHeight : 0
        );
        if (next && next > 0) setHeight(next + 2);
      } catch (_e) {
        /* cross-origin or not ready */
      }
    };

    const onLoad = () => {
      measure();
      // Re-measure a few times: images/fonts can change height after load.
      const t1 = setTimeout(measure, 120);
      const t2 = setTimeout(measure, 400);
      const t3 = setTimeout(measure, 1000);
      frame._cleanupTimers = () => {
        clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      };
    };

    frame.addEventListener('load', onLoad);
    // In case the frame was already loaded (fast path)
    if (frame.contentDocument && frame.contentDocument.readyState === 'complete') {
      onLoad();
    }

    return () => {
      frame.removeEventListener('load', onLoad);
      if (frame._cleanupTimers) frame._cleanupTimers();
    };
  }, [srcDoc]);

  return (
    <iframe
      ref={frameRef}
      title="email-message-body"
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      style={{
        display: 'block',
        width: '100%',
        height,
        border: 'none',
        background: '#fff',
        overflow: 'hidden'
      }}
    />
  );
});

const formatRelative = (v, t) => {
  if (!v) return '-';
  const now = Date.now();
  const ts = new Date(v).getTime();
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return t ? t('mailbox.justNow') : 'just now';
  if (min < 60) return t ? t('mailbox.minutesAgo', { count: min }) : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return t ? t('mailbox.hoursAgo', { count: hr }) : `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return t ? t('mailbox.daysAgo', { count: d }) : `${d}d ago`;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeEmailList = (arr) => {
  const seen = new Set();
  return (arr || []).filter((v) => {
    const email = String(v || '').trim();
    if (!email) return false;
    const k = email.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const parseEmailInput = (value) =>
  String(value || '')
    .split(/[;,]/g)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Outbound reply "To" must be the external party, not the mailbox address.
 * Some inbound rows store the mailbox as sender_email (e.g. certain Graph layouts);
 * in that case the customer is in recipient_email.
 */
const computeReplyRecipientEmail = (lastMsg, mailboxList) => {
  if (!lastMsg) return '';
  const replyMailboxId = lastMsg.mailbox_id;
  const replyMailbox = (mailboxList || []).find((m) => m.mailbox_id === replyMailboxId);
  const mailboxEmail = String(replyMailbox?.mailbox_email || '').trim();
  const senderEmail = String(lastMsg.sender_email || '').trim();
  const recipientEmail = String(lastMsg.recipient_email || '').trim();
  if (mailboxEmail && senderEmail && senderEmail.toLowerCase() === mailboxEmail.toLowerCase()) {
    return recipientEmail;
  }
  return senderEmail || recipientEmail || '';
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getMailboxReadStorageKey = () => {
  try {
    const raw = localStorage.getItem('crm_user');
    const user = raw ? JSON.parse(raw) : null;
    const id = user?.user_id || user?.email || 'anon';
    return `mailbox_read_${id}`;
  } catch (_) {
    return 'mailbox_read_anon';
  }
};

/* ─── Inline styles (CSS-in-JS) ──────────────────────────────────────────── */

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

  .mb-root * { box-sizing: border-box; }

  .mb-root {
    font-family: 'DM Sans', sans-serif;
    --c-surface: #ffffff;
    --c-surface2: #faf9f7;
    --c-border: #e8e5df;
    --c-border-soft: #f0ede8;
    --c-text: #1a1917;
    --c-text-2: #5c5956;
    --c-text-3: #9b9792;
    --c-accent: #2a5fff;
    --c-accent-soft: #eef2ff;
    --c-accent-hover: #1e4fe8;
    --c-green: #16a34a;
    --c-green-soft: #f0fdf4;
    --c-amber: #d97706;
    --c-amber-soft: #fffbeb;
    --c-red: #dc2626;
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 14px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow-md: 0 4px 12px rgba(0,0,0,.08), 0 2px 4px rgba(0,0,0,.04);
    --shadow-lg: 0 8px 32px rgba(0,0,0,.10), 0 2px 8px rgba(0,0,0,.05);
    background: var(--c-bg);
    min-height: 100vh;
    padding: 0 0 20px;
  }

  /* ── Header ── */
  .mb-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 16px 16px 0;
    margin-bottom: 12px;
  }
  .mb-header-left { display: flex; align-items: center; gap: 12px; }
  .mb-header-middle {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 280px;
    margin: 0 10px;
    flex-wrap: wrap;
  }
  .mb-header-actions { display: flex; gap: 8px; }
  .mb-header-icon {
    width: 40px; height: 40px;
    background: var(--c-accent);
    border-radius: var(--radius-md);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
    box-shadow: 0 2px 8px rgba(42,95,255,.30);
  }
  .mb-title { font-size: 22px; font-weight: 700; color: var(--c-text); margin: 0; letter-spacing: -0.4px; }
  .mb-subtitle { font-size: 13px; color: var(--c-text-3); margin: 2px 0 0; }

  /* ── Buttons ── */
  .mb-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 14px;
    border-radius: var(--radius-md);
    border: none;
    font-family: inherit; font-size: 13.5px; font-weight: 600;
    cursor: pointer; transition: all .15s ease;
    white-space: nowrap;
  }
  .mb-btn-primary {
    background: var(--c-accent); color: #fff;
    box-shadow: 0 2px 8px rgba(42,95,255,.28);
  }
  .mb-btn-primary:hover { background: var(--c-accent-hover); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(42,95,255,.35); }
  .mb-btn-primary:active { transform: translateY(0); }
  .mb-btn-ghost {
    background: var(--c-surface); color: var(--c-text-2);
    border: 1.5px solid var(--c-border);
    box-shadow: var(--shadow-sm);
  }
  .mb-btn-ghost:hover { background: var(--c-surface2); color: var(--c-text); border-color: #d0ccc4; }
  .mb-btn-sm { padding: 5px 10px; font-size: 12.5px; border-radius: var(--radius-sm); }
  .mb-btn-danger-ghost {
    background: transparent; color: var(--c-red);
    border: 1.5px solid #fecaca;
  }
  .mb-btn-danger-ghost:hover { background: #fef2f2; }

  /* ── Compose panel ── */
  .mb-compose {
    margin: 0 16px 12px;
    background: var(--c-surface);
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    overflow: hidden;
    animation: slideDown .2s ease;
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mb-compose-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid var(--c-border-soft);
    background: var(--c-surface2);
  }
  .mb-compose-title { font-size: 14px; font-weight: 700; color: var(--c-text); margin: 0; display: flex; align-items: center; gap: 8px; }
  .mb-compose-body { padding: 14px; }
  .mb-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .mb-form-full { grid-column: 1 / -1; }
  .mb-form-group { display: flex; flex-direction: column; gap: 6px; }
  .mb-label { font-size: 12px; font-weight: 600; color: var(--c-text-2); letter-spacing: .02em; text-transform: uppercase; }
  .mb-input, .mb-select, .mb-textarea {
    padding: 9px 12px;
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-sm);
    font-family: inherit; font-size: 14px; color: var(--c-text);
    background: var(--c-surface);
    transition: border-color .15s, box-shadow .15s;
    outline: none;
  }
  .mb-input:focus, .mb-select:focus, .mb-textarea:focus {
    border-color: var(--c-accent);
    box-shadow: 0 0 0 3px rgba(42,95,255,.10);
  }
  .mb-textarea { resize: vertical; min-height: 120px; line-height: 1.55; }
  .mb-compose-actions {
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
    padding: 10px 14px;
    border-top: 1px solid var(--c-border-soft);
    background: var(--c-surface2);
  }
  .mb-attach-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .mb-attach-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 8px;
    background: var(--c-accent-soft);
    border: 1.5px solid #c7d7ff;
    border-radius: var(--radius-sm);
    font-size: 12px; color: var(--c-accent); font-weight: 500;
  }
  .mb-attach-chip-remove {
    background: none; border: none; padding: 0; cursor: pointer;
    color: var(--c-accent); display: flex; align-items: center;
    opacity: .7; transition: opacity .1s;
  }
  .mb-attach-chip-remove:hover { opacity: 1; }

  /* ── Toolbar ── */
  .mb-toolbar { display: none; }
  .mb-search-wrap {
    position: relative; flex: 1; max-width: 420px; min-width: 220px;
  }
  .mb-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--c-text-3); pointer-events: none; }
  .mb-search-input {
    width: 100%; padding: 8px 10px 8px 34px;
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-md);
    font-family: inherit; font-size: 13.5px; color: var(--c-text);
    background: var(--c-surface);
    box-shadow: var(--shadow-sm);
    outline: none; transition: border-color .15s, box-shadow .15s;
  }
  .mb-search-input:focus { border-color: var(--c-accent); box-shadow: 0 0 0 3px rgba(42,95,255,.10); }
  .mb-filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .mb-filter-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px;
    background: var(--c-surface); border: 1.5px solid var(--c-border);
    border-radius: 999px;
    font-family: inherit; font-size: 13px; color: var(--c-text-2);
    cursor: pointer; outline: none; transition: all .15s;
    box-shadow: var(--shadow-sm);
  }
  .mb-filter-pill:focus, .mb-filter-pill:hover { border-color: #c0bab2; color: var(--c-text); }
  .mb-filter-pill option { font-family: inherit; }

  @media (max-width: 1320px) {
    .mb-header-middle {
      min-width: 0;
      flex: 1 1 100%;
      margin: 0;
      order: 3;
    }
    .mb-search-wrap {
      max-width: none;
      min-width: 220px;
      flex: 1 1 260px;
    }
    .mb-filter-row {
      flex: 1 1 100%;
      gap: 6px;
    }
    .mb-filter-row > svg {
      display: none;
    }
    .mb-filter-pill {
      flex: 1 1 170px;
      min-width: 150px;
    }
  }

  /* ── Main content split ── */
  .mb-content { padding: 0 16px; }
  .mb-split {
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 10px;
    height: calc(100vh - 230px);
    max-height: 860px;
    min-height: 500px;
  }

  /* ── Thread list ── */
  .mb-thread-list {
    background: var(--c-surface);
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-lg);
    overflow-y: auto; overflow-x: hidden;
    box-shadow: var(--shadow-sm);
  }
  .mb-thread-list-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--c-border-soft);
    position: sticky; top: 0; background: var(--c-surface); z-index: 1;
  }
  .mb-thread-count { font-size: 12px; font-weight: 600; color: var(--c-text-3); }
  .mb-thread-item {
    width: 100%; text-align: left;
    border: none; border-bottom: 1px solid var(--c-border-soft);
    background: transparent;
    padding: 10px 12px;
    cursor: pointer;
    transition: background .12s;
    display: block;
  }
  .mb-thread-item:last-child { border-bottom: none; }
  .mb-thread-item:hover { background: var(--c-surface2); }
  .mb-thread-item.active { background: var(--c-accent-soft); border-left: 3px solid var(--c-accent); padding-left: 9px; }
  .mb-thread-subject {
    font-size: 13.5px; font-weight: 600; color: var(--c-text);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    margin-bottom: 4px;
  }
  .mb-thread-item.active .mb-thread-subject { color: var(--c-accent); }
  .mb-thread-meta {
    font-size: 12px; color: var(--c-text-3);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    display: flex; align-items: center; gap: 4px;
  }
  .mb-thread-footer {
    display: flex; align-items: center; gap: 6px;
    margin-top: 5px;
  }
  .mb-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 6px;
    border-radius: 999px;
    font-size: 11px; font-weight: 600;
  }
  .mb-badge-gray { background: #f0ede8; color: var(--c-text-2); }
  .mb-badge-blue { background: var(--c-accent-soft); color: var(--c-accent); }
  .mb-badge-unread { background: #fee2e2; color: #991b1b; }
  .mb-badge-new { background: #dbeafe; color: #1d4ed8; }
  .mb-thread-time { font-size: 11px; color: var(--c-text-3); margin-left: auto; flex-shrink: 0; }
  .mb-thread-item-unread .mb-thread-subject { font-weight: 700; }

  /* ── Detail panel ── */
  .mb-detail {
    background: var(--c-surface);
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-lg);
    overflow-y: auto; overflow-x: hidden;
    box-shadow: var(--shadow-sm);
  }
  .mb-detail-header {
    position: sticky; top: 0; z-index: 2;
    padding: 12px 14px;
    border-bottom: 1px solid var(--c-border-soft);
    background: var(--c-surface);
  }
  .mb-detail-subject { font-size: 16px; font-weight: 700; color: var(--c-text); letter-spacing: -0.2px; margin: 0 0 4px; }
  .mb-detail-participants { font-size: 12px; color: var(--c-text-3); }
  .mb-detail-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }

  /* ── Message card ── */
  .mb-msg {
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-md);
    overflow: hidden;
    animation: fadeUp .2s ease both;
    transition: box-shadow .15s;
  }
  .mb-msg:hover { box-shadow: var(--shadow-md); }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mb-msg-reply { margin-left: 24px; }
  .mb-msg-header {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 7px 10px;
    border-bottom: 1px solid var(--c-border-soft);
  }
  .mb-msg-header-original { background: #f0f4ff; border-bottom-color: #dce7ff; }
  .mb-msg-header-reply { background: var(--c-green-soft); border-bottom-color: #bbf7d0; }
  .mb-msg-type {
    display: flex; align-items: center; gap: 7px;
    font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  }
  .mb-msg-type-original { color: var(--c-accent); }
  .mb-msg-type-reply { color: var(--c-green); }
  .mb-msg-time { font-size: 11px; color: var(--c-text-3); font-variant-numeric: tabular-nums; }
  .mb-msg-time-wrap { display: inline-flex; align-items: center; gap: 6px; }
  .mb-msg-body { padding: 10px; }
  .mb-msg-from-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 6px; }
  .mb-msg-sender {
    font-size: 14px; font-weight: 600; color: var(--c-text);
    display: flex; align-items: center; gap: 8px;
  }
  .mb-avatar {
    width: 28px; height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--c-accent), #7c5cfc);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0;
    letter-spacing: .02em;
  }
  .mb-avatar-green { background: linear-gradient(135deg, var(--c-green), #059669); }
  .mb-msg-addr { font-size: 12px; color: var(--c-text-3); margin-bottom: 12px; }
  .mb-msg-addr strong { color: var(--c-text-2); font-weight: 500; }
  /* Container around the sandboxed iframe that renders the email HTML.
     Styles only affect the wrapper, NOT the email content (which is
     isolated inside the iframe so our theme doesn't leak in). */
  .mb-msg-content {
    border-top: 1px solid var(--c-border-soft);
    padding-top: 12px; margin-top: 4px;
    border-radius: 6px;
    overflow: hidden;
  }
  .mb-msg-footer {
    display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
    padding: 8px 10px;
    border-top: 1px solid var(--c-border-soft);
    background: var(--c-surface2);
    font-size: 12px; color: var(--c-text-3);
  }
  .mb-msg-footer-item { display: flex; align-items: center; gap: 5px; }
  .mb-link {
    color: var(--c-accent); text-decoration: none; font-weight: 500;
    background: none; border: none; padding: 0; cursor: pointer; font-size: inherit; font-family: inherit;
  }
  .mb-link:hover { text-decoration: underline; }

  /* ── Attachments ── */
  .mb-att-section { margin-top: 12px; }
  .mb-att-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: var(--c-text-3); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
  .mb-att-list { display: flex; flex-direction: column; gap: 7px; }
  .mb-att-item {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    padding: 7px 10px;
    background: var(--c-surface2);
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-sm);
    font-size: 13px;
  }
  .mb-att-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; color: var(--c-text); }
  .mb-att-size { color: var(--c-text-3); font-size: 11.5px; flex-shrink: 0; font-family: 'DM Mono', monospace; }
  .mb-att-fallback-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 7px;
    background: var(--c-surface2); border: 1.5px solid var(--c-border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--c-text-2);
  }

  /* ── Empty / loading states ── */
  .mb-empty {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; gap: 12px; color: var(--c-text-3);
  }
  .mb-empty-icon { width: 56px; height: 56px; background: var(--c-surface2); border: 2px dashed var(--c-border); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
  .mb-spinner {
    width: 32px; height: 32px;
    border: 3px solid var(--c-border);
    border-top-color: var(--c-accent);
    border-radius: 50%;
    animation: spin .7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Footer status bar ── */
  .mb-statusbar {
    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
    padding: 10px 16px 0;
    font-size: 12.5px; color: var(--c-text-3);
  }
  .mb-statusbar-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--c-border); flex-shrink: 0; }
  .mb-statusbar-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
  }

  /* ── Scrollbar polish ── */
  .mb-thread-list::-webkit-scrollbar,
  .mb-detail::-webkit-scrollbar { width: 5px; }
  .mb-thread-list::-webkit-scrollbar-track,
  .mb-detail::-webkit-scrollbar-track { background: transparent; }
  .mb-thread-list::-webkit-scrollbar-thumb,
  .mb-detail::-webkit-scrollbar-thumb { background: var(--c-border); border-radius: 999px; }

  @media (max-width: 860px) {
    .mb-split { grid-template-columns: 1fr; height: auto; }
    .mb-header, .mb-content, .mb-statusbar { padding-left: 16px; padding-right: 16px; }
    .mb-header-middle { order: 3; width: 100%; margin: 4px 0 0; }
    .mb-header-actions { margin-left: auto; }
    .mb-filter-row { width: 100%; }
    .mb-filter-pill { flex: 1 1 calc(50% - 4px); min-width: 120px; }
    .mb-form-grid { grid-template-columns: 1fr; }
    .mb-compose { margin-left: 16px; margin-right: 16px; }
  }
`;

/* ─── MailboxList ─────────────────────────────────────────────────────────── */

const MailboxList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isAdminView, setIsAdminView] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [sending, setSending] = useState(false);
  const [replying, setReplying] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileRef = useRef(null);
  const [composeForm, setComposeForm] = useState({ mailbox_id: '', to: '', cc: '', subject: '', body_html: '' });
  const [manualToEmails, setManualToEmails] = useState('');
  const [studentRecipientQuery, setStudentRecipientQuery] = useState('');
  const [agencyRecipientQuery, setAgencyRecipientQuery] = useState('');
  const [studentRecipientOptions, setStudentRecipientOptions] = useState([]);
  const [agencyRecipientOptions, setAgencyRecipientOptions] = useState([]);
  const [selectedStudentRecipientEmails, setSelectedStudentRecipientEmails] = useState([]);
  const [selectedAgencyRecipientEmails, setSelectedAgencyRecipientEmails] = useState([]);
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const studentDropdownRef = useRef(null);
  const agencyDropdownRef = useRef(null);
  const [composeFiles, setComposeFiles] = useState([]);
  // Compose body view mode: 'design' (Quill rich editor), 'html' (raw HTML
  // source textarea), or 'preview' (sandboxed iframe render). When a selected
  // template is rich HTML we auto-switch to 'preview' so the design isn't
  // destroyed by Quill's tag/format whitelist.
  const [composeBodyMode, setComposeBodyMode] = useState('design');
  const [filters, setFilters] = useState({ search: '', direction: '', mailbox_id: '', read_status: '' });
  const [mailboxes, setMailboxes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [sendWindowStatus, setSendWindowStatus] = useState(null);
  const [userSignature, setUserSignature] = useState('');
  const readStorageKey = useMemo(() => getMailboxReadStorageKey(), []);
  const readMapInitialised = useRef(false);
  const [readMap, setReadMap] = useState(() => {
    try {
      const key = getMailboxReadStorageKey();
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  });

  useEffect(() => {
    if (!readMapInitialised.current) {
      readMapInitialised.current = true;
      return;
    }
    try {
      localStorage.setItem(readStorageKey, JSON.stringify(readMap));
      window.dispatchEvent(new Event('mailbox-read-updated'));
    } catch (_) {}
  }, [readStorageKey, readMap]);

  const unreadIncomingIds = useMemo(() => {
    const ids = new Set();
    items.forEach((m) => {
      if (m.direction === 'RECEIVED' && !readMap[m.email_log_id]) {
        ids.add(m.email_log_id);
      }
    });
    return ids;
  }, [items, readMap]);

  const threads = useMemo(() => {
    const grouped = new Map();
    items.forEach((row) => {
      const key = row.conversation_id || `solo_${row.email_log_id}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(row);
    });
    const list = Array.from(grouped.entries()).map(([id, msgs]) => {
      const sortedAsc = [...msgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const latest = sortedAsc[sortedAsc.length - 1];
      return {
        threadId: id,
        messages: sortedAsc,
        latest,
        first: sortedAsc[0],
        subject: sortedAsc[0]?.subject || t('mailbox.noSubject'),
        participants: [...new Set(sortedAsc.map((m) => m.sender_email).filter(Boolean))],
        unreadCount: sortedAsc.filter((m) => unreadIncomingIds.has(m.email_log_id)).length
      };
    });
    list.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
    return list;
  }, [items, unreadIncomingIds]);

  const filteredThreads = useMemo(() => {
    if (!filters.read_status) return threads;
    if (filters.read_status === 'unread') return threads.filter((t) => t.unreadCount > 0);
    return threads.filter((t) => t.unreadCount === 0);
  }, [threads, filters.read_status]);

  useEffect(() => {
    if (!filteredThreads.length) { setSelectedThreadId(''); return; }
    if (!selectedThreadId || !filteredThreads.some((t) => t.threadId === selectedThreadId)) {
      setSelectedThreadId(filteredThreads[0].threadId);
    }
  }, [filteredThreads, selectedThreadId]);

  const selectedThread = useMemo(
    () => filteredThreads.find((t) => t.threadId === selectedThreadId) || null,
    [filteredThreads, selectedThreadId]
  );
  const replyPreview = useMemo(() => {
    if (!selectedThread) return { mailboxEmail: '', toEmail: '' };
    const lastReceived = [...selectedThread.messages].reverse().find(m => m.direction === 'RECEIVED') || selectedThread.latest;
    const replyMailboxId = lastReceived.mailbox_id || selectedThread.first.mailbox_id;
    const replyMailbox = mailboxes.find(m => m.mailbox_id === replyMailboxId);
    const mailboxEmail = String(replyMailbox?.mailbox_email || '');
    const toEmail = computeReplyRecipientEmail(lastReceived, mailboxes);
    return { mailboxEmail, toEmail };
  }, [selectedThread, mailboxes]);

  const canReplyToSelected = useMemo(() => {
    if (!selectedThread) return false;
    const lastReceived = [...selectedThread.messages].reverse().find(m => m.direction === 'RECEIVED') || selectedThread.latest;
    const mboxId = lastReceived.mailbox_id || selectedThread.first.mailbox_id;
    const mbox = mailboxes.find(m => m.mailbox_id === mboxId);
    if (mbox && !canUserSendFromMailbox(mbox, user)) return false;
    return true;
  }, [selectedThread, mailboxes, user]);

  const canSendByFrenchSchedule = !sendWindowStatus || sendWindowStatus.allowed;
  const showFrenchHoursBanner = sendWindowStatus && !sendWindowStatus.allowed && sendWindowStatus.applies_to_me;

  useEffect(() => {
    if (!selectedThread) return;
    const inboundIds = selectedThread.messages
      .filter((m) => m.direction === 'RECEIVED')
      .map((m) => m.email_log_id);
    const conversationId = selectedThread.messages.find((m) => m.conversation_id)?.conversation_id || null;
    const mailboxId = selectedThread.first?.mailbox_id || selectedThread.latest?.mailbox_id || null;

    setReadMap((prev) => {
      const idsToMark = inboundIds.filter((id) => !prev[id]);
      if (!idsToMark.length) return prev;
      const next = { ...prev };
      idsToMark.forEach((id) => { next[id] = Date.now(); });
      return next;
    });

    // Server-side: clear bell + sidebar mailbox notifications tied to this thread/messages.
    // Run in the background — UI doesn't depend on the response.
    if (inboundIds.length) {
      notificationsAPI
        .markMailboxThreadRead({
          email_log_ids: inboundIds,
          conversation_id: conversationId,
          mailbox_id: mailboxId
        })
        .catch(() => {});
    }
  }, [selectedThread]);

  const refreshSendWindow = useCallback(async () => {
    try {
      const res = await emailsAPI.getSendWindowStatus();
      setSendWindowStatus(res.data || null);
    } catch (_) {
      setSendWindowStatus(null);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [feedRes, boxesRes, templatesRes] = await Promise.all([
        emailsAPI.getMailboxFeed(params),
        emailsAPI.getMailboxes(),
        emailTemplatesAPI.list()
      ]);
      setItems(feedRes.data?.data || []);
      setTotal(feedRes.data?.total || 0);
      setIsAdminView(!!feedRes.data?.is_admin_view);
      const boxes = boxesRes.data || [];
      const templateRows = templatesRes.data || [];
      setMailboxes(boxes);
      setTemplates(templateRows);
      const sendable = boxes.filter(m => canUserSendFromMailbox(m, user));
      setComposeForm((prev) => ({
        ...prev,
        mailbox_id: prev.mailbox_id || String(sendable[0]?.mailbox_id || boxes[0]?.mailbox_id || '')
      }));
      setSelectedTemplateId('');

      // If a mailbox filter is selected but user cannot access it (per Mail Configuration), clear it.
      if (filters.mailbox_id) {
        const selected = boxes.find((m) => String(m.mailbox_id) === String(filters.mailbox_id));
        if (selected && !canUserSendFromMailbox(selected, user)) {
          setFilters((f) => ({ ...f, mailbox_id: '' }));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailbox.loadFailed'));
    } finally {
      setLoading(false);
      refreshSendWindow();
    }
  }, [filters, refreshSendWindow]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { refreshSendWindow(); }, [refreshSendWindow]);
  useEffect(() => {
    authAPI.getSignature()
      .then((res) => setUserSignature(res.data?.signature_html || ''))
      .catch(() => {});
  }, []);

  const sendableMailboxes = useMemo(() =>
    mailboxes.filter(m => canUserSendFromMailbox(m, user)),
    [mailboxes, user]
  );

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!studentDropdownOpen) return;
      const q = String(studentRecipientQuery || '').trim();
      try {
        const res = await emailsAPI.searchRecipientStudents(q);
        setStudentRecipientOptions(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setStudentRecipientOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [studentRecipientQuery, studentDropdownOpen]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!agencyDropdownOpen) return;
      const q = String(agencyRecipientQuery || '').trim();
      try {
        const res = await emailsAPI.searchRecipientAgencies(q);
        setAgencyRecipientOptions(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setAgencyRecipientOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [agencyRecipientQuery, agencyDropdownOpen]);

  useEffect(() => {
    const merged = normalizeEmailList([
      ...parseEmailInput(manualToEmails),
      ...selectedStudentRecipientEmails,
      ...selectedAgencyRecipientEmails
    ]);
    setComposeForm((prev) => ({ ...prev, to: merged.join(', ') }));
  }, [manualToEmails, selectedStudentRecipientEmails, selectedAgencyRecipientEmails]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target)) {
        setStudentDropdownOpen(false);
      }
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(event.target)) {
        setAgencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Mailbox filter dropdown should respect access config (all/admin_only/specific_users).
  const accessibleMailboxes = useMemo(() => {
    // Admins can see everything in the dropdown.
    const role = String(user?.role || '').toLowerCase();
    if (['super_admin', 'admin'].includes(role)) return mailboxes;
    return mailboxes.filter((m) => canUserSendFromMailbox(m, user));
  }, [mailboxes, user]);

  const buildSendFormData = () => {
    const fd = new FormData();
    fd.append('mailbox_id', composeForm.mailbox_id);
    fd.append('to', composeForm.to);
    fd.append('cc', composeForm.cc || '');
    fd.append('subject', composeForm.subject);
    fd.append('body_html', composeForm.body_html || '');
    composeFiles.forEach((f) => fd.append('attachments', f));
    return fd;
  };

  const handleSendEmail = async () => {
    if (!canSendByFrenchSchedule) {
      toast.error(sendWindowStatus?.message || t('mailbox.sendWindowBlocked'));
      return;
    }
    if (!composeForm.mailbox_id || !composeForm.to || !composeForm.subject) {
      toast.error(t('mailbox.composeRequired'));
      return;
    }
    setSending(true);
    try {
      await emailsAPI.send(buildSendFormData());
      if (selectedTemplateId) {
        await emailTemplatesAPI.markUsed(selectedTemplateId, { context_type: 'mailbox', context_id: null });
      }
      toast.success(t('mailbox.emailSent'));
      setComposeForm((prev) => ({ ...prev, cc: '', subject: '', body_html: '' }));
      setManualToEmails('');
      setComposeFiles([]);
      setStudentRecipientQuery('');
      setAgencyRecipientQuery('');
      setStudentRecipientOptions([]);
      setAgencyRecipientOptions([]);
      setSelectedStudentRecipientEmails([]);
      setSelectedAgencyRecipientEmails([]);
      setStudentDropdownOpen(false);
      setAgencyDropdownOpen(false);
      setShowCompose(false);
      setComposeBodyMode('design');
      setSelectedTemplateId('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailbox.sendFailed'));
    }
    setSending(false);
  };

  const handleReplyEmail = async () => {
    if (!canSendByFrenchSchedule) {
      toast.error(sendWindowStatus?.message || t('mailbox.sendWindowBlocked'));
      return;
    }
    if (isEmailBodyEmpty(replyText)) { toast.error(t('mailbox.replyRequired')); return; }
    if (!selectedThread) return;
    const lastReceived = [...selectedThread.messages].reverse().find(m => m.direction === 'RECEIVED') || selectedThread.latest;
    const lastWithGraphId = [...selectedThread.messages].reverse().find(m => m.graph_message_id);
    const replyMailboxId = lastReceived.mailbox_id || selectedThread.first.mailbox_id;
    const replyMailbox = mailboxes.find(m => m.mailbox_id === replyMailboxId);
    if (replyMailbox && !canUserSendFromMailbox(replyMailbox, user)) {
      toast.error(t('mailbox.noReplyPermission'));
      return;
    }
    setReplying(true);
    try {
      const fd = new FormData();
      fd.append('mailbox_id', replyMailboxId);
      fd.append('to', computeReplyRecipientEmail(lastReceived, mailboxes));
      fd.append('subject', selectedThread.subject);
      fd.append('body_html', replyText);
      const convId = selectedThread.threadId.startsWith('solo_') ? '' : selectedThread.threadId;
      if (convId) fd.append('conversation_id', convId);
      if (lastWithGraphId?.graph_message_id) fd.append('reply_to_graph_message_id', lastWithGraphId.graph_message_id);
      if (lastReceived.lead_id) fd.append('lead_id', lastReceived.lead_id);
      if (lastReceived.student_id) fd.append('student_id', lastReceived.student_id);
      replyFiles.forEach(f => fd.append('attachments', f));
      await emailsAPI.reply(fd);
      toast.success(t('mailbox.replySent'));
      setReplyText('');
      setReplyFiles([]);
      setShowReplyForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailbox.replyFailed'));
    }
    setReplying(false);
  };

  const getInitials = (email) => (email || '?').split('@')[0].slice(0, 2).toUpperCase();

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const rendered = await emailTemplatesAPI.render(templateId, {});
      const body = rendered.data?.body || '';
      setComposeForm((f) => ({
        ...f,
        subject: rendered.data?.subject || '',
        body_html: body
      }));
      // Rich HTML designs can't render inside Quill — switch to the iframe
      // preview so the user sees the real design. Simple bodies stay in the
      // Design editor so the user can keep typing normally.
      setComposeBodyMode(isRichEmailHtml(body) ? 'preview' : 'design');
      await emailTemplatesAPI.markUsed(templateId, { context_type: 'mailbox', context_id: null });
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailbox.applyTemplateFailed'));
    }
  };

  const studentOptionLabel = (s) => {
    const name = `${String(s?.first_name || '').trim()} ${String(s?.last_name || '').trim()}`.trim() || String(s?.student_ref || '-');
    const email = String(s?.email || '-').trim() || '-';
    const phone = [String(s?.phone_country_code || '').trim(), String(s?.phone || '').trim()].filter(Boolean).join(' ') || '-';
    return `${name} - ${email} - ${phone}`;
  };

  const agencyOptionLabel = (a) => {
    const name = String(a?.student_name || a?.ref_number || '-').trim() || '-';
    const email = String(a?.email || '-').trim() || '-';
    const phone = [String(a?.phone_country_code || '').trim(), String(a?.contact_number || '').trim()].filter(Boolean).join(' ') || '-';
    return `${name} - ${email} - ${phone}`;
  };

  const studentSelectableOptions = useMemo(
    () => (studentRecipientOptions || []).filter((opt) => String(opt?.email || '').trim()),
    [studentRecipientOptions]
  );
  const agencySelectableOptions = useMemo(
    () => (agencyRecipientOptions || []).filter((opt) => String(opt?.email || '').trim()),
    [agencyRecipientOptions]
  );

  const toggleStudentEmail = (email) => {
    const next = String(email || '').trim();
    if (!next) return;
    setSelectedStudentRecipientEmails((prev) => (
      prev.some((e) => String(e).toLowerCase() === next.toLowerCase())
        ? prev.filter((e) => String(e).toLowerCase() !== next.toLowerCase())
        : normalizeEmailList([...prev, next])
    ));
  };

  const toggleAgencyEmail = (email) => {
    const next = String(email || '').trim();
    if (!next) return;
    setSelectedAgencyRecipientEmails((prev) => (
      prev.some((e) => String(e).toLowerCase() === next.toLowerCase())
        ? prev.filter((e) => String(e).toLowerCase() !== next.toLowerCase())
        : normalizeEmailList([...prev, next])
    ));
  };

  return (
    <div className="mb-root">
      <style>{styles}</style>

      {/* ── Page header ── */}
      <div className="mb-header">
        <div className="mb-header-left">
          <div className="mb-header-icon"><FiInbox size={18} /></div>
          <div>
            <h1 className="mb-title">{t('mailbox.title')}</h1>
            <p className="mb-subtitle">{t('mailbox.subtitle')}</p>
          </div>
        </div>
        <div className="mb-header-middle">
          <div className="mb-search-wrap">
            <FiSearch size={14} className="mb-search-icon" />
            <input
              className="mb-search-input"
              placeholder={t('mailbox.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <div className="mb-filter-row">
            <FiFilter size={13} style={{ color: 'var(--c-text-3)' }} />
            <select
              className="mb-filter-pill"
              value={filters.direction}
              onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value }))}
            >
              <option value="">{t('mailbox.allDirections')}</option>
              <option value="SENT">{t('mailbox.sent')}</option>
              <option value="RECEIVED">{t('mailbox.received')}</option>
            </select>
            <select
              className="mb-filter-pill"
              value={filters.mailbox_id}
              onChange={(e) => setFilters((f) => ({ ...f, mailbox_id: e.target.value }))}
            >
              <option value="">{t('mailbox.allMailboxes')}</option>
              {accessibleMailboxes.map((m) => (
                <option key={m.mailbox_id} value={m.mailbox_id}>
                  {m.mailbox_name} ({m.mailbox_email})
                </option>
              ))}
            </select>
            <select
              className="mb-filter-pill"
              value={filters.read_status}
              onChange={(e) => setFilters((f) => ({ ...f, read_status: e.target.value }))}
            >
              <option value="">{t('mailbox.allStatus')}</option>
              <option value="unread">{t('mailbox.unread')}</option>
              <option value="read">{t('mailbox.read')}</option>
            </select>
          </div>
        </div>
        <div className="mb-header-actions">
          <button type="button" className="mb-btn mb-btn-ghost" onClick={loadData} title={t('mailbox.refresh')}>
            <FiRefreshCw size={14} />
          </button>
          <button
            type="button"
            className={`mb-btn ${showCompose ? 'mb-btn-ghost' : 'mb-btn-primary'}`}
            onClick={() => {
              if (!showCompose && !canSendByFrenchSchedule) {
                toast.error(sendWindowStatus?.message || t('mailbox.sendWindowBlocked'));
                return;
              }
              setShowCompose((v) => !v);
              setSelectedTemplateId('');
              setComposeBodyMode('design');
            }}
          >
            {showCompose ? <><FiX size={15} /> {t('mailbox.closeCompose')}</> : <><FiEdit3 size={15} /> {t('mailbox.compose')}</>}
          </button>
        </div>
      </div>

      {showFrenchHoursBanner && sendWindowStatus?.message && (
        <div
          style={{
            margin: '0 16px 12px',
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: 13,
            lineHeight: 1.5
          }}
        >
          <strong>{t('mailbox.sendHours')}</strong>
          <div style={{ marginTop: 4 }}>{sendWindowStatus.message}</div>
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>{sendWindowStatus.schedule_label}</div>
        </div>
      )}

      {/* ── Compose panel ── */}
      {showCompose && (
        <div className="mb-compose">
          <div className="mb-compose-header">
            <h3 className="mb-compose-title"><FiEdit3 size={14} /> {t('mailbox.newEmail')}</h3>
            <button type="button" className="mb-btn mb-btn-ghost mb-btn-sm" onClick={() => { setShowCompose(false); setComposeFiles([]); setComposeBodyMode('design'); setSelectedTemplateId(''); setStudentRecipientQuery(''); setAgencyRecipientQuery(''); setStudentRecipientOptions([]); setAgencyRecipientOptions([]); setSelectedStudentRecipientEmails([]); setSelectedAgencyRecipientEmails([]); setStudentDropdownOpen(false); setAgencyDropdownOpen(false); setManualToEmails(''); setComposeForm((f) => ({ ...f, to: '' })); }}>
              <FiX size={13} /> {t('mailbox.discard')}
            </button>
          </div>
          <div className="mb-compose-body">
            <div className="mb-form-group mb-form-full" style={{ marginBottom: 10 }}>
              <label className="mb-label">{t('mailbox.templateLabel')}</label>
              <select
                className="mb-select"
                value={selectedTemplateId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedTemplateId(v);
                  if (v) applyTemplate(v);
                }}
              >
                <option value="">{t('mailbox.selectTemplate')}</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-form-grid">
              <div className="mb-form-group">
                <label className="mb-label">{t('mailbox.fromLabel')}</label>
                <select
                  className="mb-select"
                  value={composeForm.mailbox_id}
                  onChange={(e) => setComposeForm((f) => ({ ...f, mailbox_id: e.target.value }))}
                >
                  <option value="">{t('mailbox.selectMailbox')}</option>
                  {sendableMailboxes.map((m) => (
                    <option key={m.mailbox_id} value={m.mailbox_id}>
                      {m.mailbox_name} ({m.mailbox_email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-form-group">
                <label className="mb-label">{t('mailbox.toLabel')}</label>
                <input
                  type="text"
                  className="mb-input"
                  value={manualToEmails}
                  onChange={(e) => setManualToEmails(e.target.value)}
                  placeholder={t('mailbox.multiRecipientToPlaceholder', { defaultValue: 'Type emails or select students/agencies below' })}
                />
              </div>
              <div className="mb-form-group">
                <label className="mb-label">{t('mailbox.studentRecipients', { defaultValue: 'Students (Multi-select)' })}</label>
                <div ref={studentDropdownRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="mb-btn mb-btn-ghost"
                    style={{ width: '100%', justifyContent: 'space-between' }}
                    onClick={() => setStudentDropdownOpen((v) => !v)}
                  >
                    <span>
                      {selectedStudentRecipientEmails.length > 0
                        ? t('mailbox.selectedCount', { defaultValue: '{{count}} selected', count: selectedStudentRecipientEmails.length })
                        : t('mailbox.selectStudentsPrompt', { defaultValue: 'Select students' })}
                    </span>
                    <FiChevronRight size={14} style={{ transform: studentDropdownOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                  </button>
                  {studentDropdownOpen && (
                    <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          className="mb-btn mb-btn-ghost mb-btn-sm"
                          onClick={() => setSelectedStudentRecipientEmails(normalizeEmailList([
                            ...selectedStudentRecipientEmails,
                            ...studentSelectableOptions.map((opt) => String(opt.email || '').trim())
                          ]))}
                        >
                          {t('common.selectAll', { defaultValue: 'Select All' })}
                        </button>
                        <input
                          type="text"
                          className="mb-input"
                          value={studentRecipientQuery}
                          onChange={(e) => setStudentRecipientQuery(e.target.value)}
                          placeholder={t('mailbox.searchStudentsRecipients', { defaultValue: 'Search students by name, ref, email or phone' })}
                        />
                        <button
                          type="button"
                          className="mb-btn mb-btn-ghost mb-btn-sm"
                          onClick={() => setSelectedStudentRecipientEmails([])}
                        >
                          {t('common.clearAll', { defaultValue: 'Clear All' })}
                        </button>
                      </div>
                      <div style={{ marginTop: 8, maxHeight: 210, overflowY: 'auto', border: '1px solid var(--c-border-soft)', borderRadius: 6, padding: 6 }}>
                        {studentRecipientOptions.length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{t('common.noResults', { defaultValue: 'No results found' })}</div>
                        ) : (
                          studentRecipientOptions.map((opt) => {
                            const email = String(opt.email || '').trim();
                            const disabled = !email;
                            const checked = selectedStudentRecipientEmails.some((e) => String(e).toLowerCase() === email.toLowerCase());
                            return (
                              <label key={`student-check-${opt.student_id}-${email || 'no-email'}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 4px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
                                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleStudentEmail(email)} />
                                <span style={{ fontSize: 12, color: 'var(--c-text)' }}>{studentOptionLabel(opt)}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-form-group">
                <label className="mb-label">{t('mailbox.agencyRecipients', { defaultValue: 'B2B Agencies (Multi-select)' })}</label>
                <div ref={agencyDropdownRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="mb-btn mb-btn-ghost"
                    style={{ width: '100%', justifyContent: 'space-between' }}
                    onClick={() => setAgencyDropdownOpen((v) => !v)}
                  >
                    <span>
                      {selectedAgencyRecipientEmails.length > 0
                        ? t('mailbox.selectedCount', { defaultValue: '{{count}} selected', count: selectedAgencyRecipientEmails.length })
                        : t('mailbox.selectAgenciesPrompt', { defaultValue: 'Select agencies' })}
                    </span>
                    <FiChevronRight size={14} style={{ transform: agencyDropdownOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                  </button>
                  {agencyDropdownOpen && (
                    <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          className="mb-btn mb-btn-ghost mb-btn-sm"
                          onClick={() => setSelectedAgencyRecipientEmails(normalizeEmailList([
                            ...selectedAgencyRecipientEmails,
                            ...agencySelectableOptions.map((opt) => String(opt.email || '').trim())
                          ]))}
                        >
                          {t('common.selectAll', { defaultValue: 'Select All' })}
                        </button>
                        <input
                          type="text"
                          className="mb-input"
                          value={agencyRecipientQuery}
                          onChange={(e) => setAgencyRecipientQuery(e.target.value)}
                          placeholder={t('mailbox.searchAgenciesRecipients', { defaultValue: 'Search agencies by name, ref, email or phone' })}
                        />
                        <button
                          type="button"
                          className="mb-btn mb-btn-ghost mb-btn-sm"
                          onClick={() => setSelectedAgencyRecipientEmails([])}
                        >
                          {t('common.clearAll', { defaultValue: 'Clear All' })}
                        </button>
                      </div>
                      <div style={{ marginTop: 8, maxHeight: 210, overflowY: 'auto', border: '1px solid var(--c-border-soft)', borderRadius: 6, padding: 6 }}>
                        {agencyRecipientOptions.length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{t('common.noResults', { defaultValue: 'No results found' })}</div>
                        ) : (
                          agencyRecipientOptions.map((opt) => {
                            const email = String(opt.email || '').trim();
                            const disabled = !email;
                            const checked = selectedAgencyRecipientEmails.some((e) => String(e).toLowerCase() === email.toLowerCase());
                            return (
                              <label key={`agency-check-${opt.lead_id}-${email || 'no-email'}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 4px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
                                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleAgencyEmail(email)} />
                                <span style={{ fontSize: 12, color: 'var(--c-text)' }}>{agencyOptionLabel(opt)}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-form-group">
                <label className="mb-label">{t('mailbox.ccLabel')}</label>
                <input
                  type="text"
                  className="mb-input"
                  value={composeForm.cc || ''}
                  onChange={(e) => setComposeForm((f) => ({ ...f, cc: e.target.value }))}
                  placeholder="cc1@email.com, cc2@email.com (optional)"
                />
              </div>
              <div className="mb-form-group mb-form-full">
                <label className="mb-label">{t('mailbox.subjectLabel')}</label>
                <input
                  className="mb-input"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder={t('mailbox.subjectPlaceholder')}
                />
              </div>
              <div className="mb-form-group mb-form-full">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                  <label className="mb-label" style={{ margin: 0 }}>{t('mailbox.messageLabel')}</label>
                  {/* Body view mode tabs: Design / HTML / Preview.
                      These let the sender keep using the rich editor for plain
                      messages, OR faithfully render an HTML-designed template
                      (which Quill would otherwise strip). */}
                  <div
                    role="tablist"
                    style={{
                      display: 'inline-flex',
                      gap: 4,
                      padding: 3,
                      borderRadius: 8,
                      background: 'var(--bg-secondary, #f1f5f9)'
                    }}
                  >
                    {[
                      { id: 'design', icon: <FiLayout size={13} />, label: t('mailbox.tabDesign', { defaultValue: 'Design' }) },
                      { id: 'html', icon: <FiCode size={13} />, label: t('mailbox.tabHtml', { defaultValue: 'HTML' }) },
                      { id: 'preview', icon: <FiEye size={13} />, label: t('mailbox.tabPreview', { defaultValue: 'Preview' }) }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={composeBodyMode === tab.id}
                        onClick={() => setComposeBodyMode(tab.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: composeBodyMode === tab.id ? 'var(--white, #fff)' : 'transparent',
                          color: composeBodyMode === tab.id ? 'var(--primary, #4f46e5)' : 'var(--text-muted, #64748b)',
                          boxShadow: composeBodyMode === tab.id ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
                        }}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {composeBodyMode === 'design' && (
                  <>
                    {isRichEmailHtml(composeForm.body_html) && (
                      <div
                        style={{
                          marginBottom: 8,
                          padding: '8px 10px',
                          fontSize: 12,
                          lineHeight: 1.4,
                          background: '#fffbeb',
                          border: '1px solid #fcd34d',
                          color: '#92400e',
                          borderRadius: 8
                        }}
                      >
                        {t('mailbox.richHtmlWarning', {
                          defaultValue:
                            'This template uses HTML (tables/styles) the simple editor cannot display. Switch to Preview to see the real design — it will be sent exactly as designed.'
                        })}
                      </div>
                    )}
                    <EmailBodyEditor
                      themeMailbox
                      value={composeForm.body_html}
                      onChange={(html) => setComposeForm((f) => ({ ...f, body_html: html }))}
                      placeholder={t('mailbox.messagePlaceholder')}
                      minHeight={280}
                    />
                  </>
                )}

                {composeBodyMode === 'html' && (
                  <textarea
                    value={composeForm.body_html || ''}
                    onChange={(e) => setComposeForm((f) => ({ ...f, body_html: e.target.value }))}
                    placeholder={'<table width="100%" style="font-family:Arial,sans-serif;...">...</table>'}
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    rows={16}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 360,
                      minHeight: 320,
                      padding: 12,
                      fontFamily: '"Menlo", "Consolas", "Courier New", monospace',
                      fontSize: 13,
                      lineHeight: 1.55,
                      border: '1.5px solid var(--input-border, #e2e8f0)',
                      borderRadius: 8,
                      resize: 'vertical',
                      background: '#ffffff',
                      color: '#0f172a',
                      outline: 'none',
                      boxSizing: 'border-box',
                      whiteSpace: 'pre',
                      overflow: 'auto'
                    }}
                  />
                )}

                {composeBodyMode === 'preview' && (
                  <div
                    style={{
                      width: '100%',
                      border: '1px solid var(--border, #e2e8f0)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#fff'
                    }}
                  >
                    <div
                      style={{
                        padding: '8px 12px',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        color: 'var(--text-muted, #64748b)',
                        background: 'var(--bg-secondary, #f8fafc)',
                        borderBottom: '1px solid var(--border, #e2e8f0)'
                      }}
                    >
                      {t('mailbox.previewLabel', { defaultValue: 'Email preview' })}
                    </div>
                    <iframe
                      title="compose-body-preview"
                      sandbox=""
                      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;width:100%;}body{padding:16px;font-family:Arial,Helvetica,sans-serif;color:#0f172a;background:#fff;box-sizing:border-box;overflow-x:auto;}img,table{max-width:100%;height:auto;}*{box-sizing:border-box;}</style></head><body>${
                        composeForm.body_html ||
                        `<p style="color:#94a3b8">${t('mailbox.previewEmpty', {
                          defaultValue: '(empty body — pick a template or type your message)'
                        })}</p>`
                      }</body></html>`}
                      style={{ display: 'block', width: '100%', height: 420, border: 'none', background: '#fff' }}
                    />
                  </div>
                )}
                {userSignature && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#f9fafb', border: '1px dashed var(--c-border)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', marginBottom: 6 }}>{t('common.signaturePreview')}</div>
                    <div dangerouslySetInnerHTML={{ __html: userSignature }} />
                  </div>
                )}
              </div>
            </div>

            {composeFiles.length > 0 && (
              <div className="mb-attach-list">
                {composeFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="mb-attach-chip">
                    <FiFile size={12} />
                    <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ opacity: .65, fontSize: 11 }}>{formatFileSize(f.size)}</span>
                    <button
                      type="button"
                      className="mb-attach-chip-remove"
                      aria-label="Remove"
                      onClick={() => setComposeFiles((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-compose-actions">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => setComposeFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
              />
              <button type="button" className="mb-btn mb-btn-ghost mb-btn-sm" onClick={() => fileInputRef.current?.click()}>
                <FiPaperclip size={13} /> {t('mailbox.attachFiles')}
              </button>
              {userSignature && <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{t('mailbox.signatureAutoAdded')}</span>}
            </div>
            <button type="button" className="mb-btn mb-btn-primary" onClick={handleSendEmail} disabled={sending || !canSendByFrenchSchedule}>
              <FiSend size={14} />
              {sending ? t('mailbox.sendingBtn') : t('mailbox.sendEmail')}
            </button>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="mb-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <div className="mb-spinner" />
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="mb-empty" style={{ minHeight: 400 }}>
            <div className="mb-empty-icon"><FiMail size={24} style={{ color: 'var(--c-text-3)' }} /></div>
            <span style={{ fontWeight: 600, color: 'var(--c-text-2)' }}>{t('mailbox.noEmails')}</span>
            <span style={{ fontSize: 13 }}>{t('mailbox.adjustFilters')}</span>
          </div>
        ) : (
          <div className="mb-split">
            {/* Thread list */}
            <div className="mb-thread-list">
              <div className="mb-thread-list-header">
                <span className="mb-thread-count">{filteredThreads.length} {t('mailbox.threadCount', { count: filteredThreads.length })}</span>
                <span className="mb-badge mb-badge-gray">{t('mailbox.totalEmails', { count: total })}</span>
              </div>
              {filteredThreads.map((thread) => (
                <button
                  key={thread.threadId}
                  type="button"
                  className={`mb-thread-item${selectedThreadId === thread.threadId ? ' active' : ''}${thread.unreadCount > 0 ? ' mb-thread-item-unread' : ''}`}
                  onClick={() => setSelectedThreadId(thread.threadId)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                    <div className="mb-thread-subject">{thread.subject}</div>
                    <span className="mb-thread-time">{formatRelative(thread.latest.created_at, t)}</span>
                  </div>
                  <div className="mb-thread-meta">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {thread.latest.sender_email} → {thread.latest.recipient_email}
                    </span>
                  </div>
                  <div className="mb-thread-footer">
                    <span className="mb-badge mb-badge-gray">{t('mailbox.msgCount', { count: thread.messages.length })}</span>
                    {thread.unreadCount > 0 && (
                      <span className="mb-badge mb-badge-unread">{t('mailbox.unreadCount', { count: thread.unreadCount })}</span>
                    )}
                    {thread.unreadCount > 0 && thread.messages.some((m) => unreadIncomingIds.has(m.email_log_id) && (Date.now() - new Date(m.created_at).getTime()) <= ONE_DAY_MS) && (
                      <span className="mb-badge mb-badge-new">{t('mailbox.newBadge')}</span>
                    )}
                    {thread.first.mailbox_name && (
                      <span className="mb-badge mb-badge-blue">{thread.first.mailbox_name}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            <div className="mb-detail">
              {selectedThread ? (
                <>
                  <div className="mb-detail-header">
                    <div className="mb-detail-subject">{selectedThread.subject}</div>
                    <div className="mb-detail-participants">
                      {selectedThread.participants.join(' · ')} · {t('mailbox.messageCount', { count: selectedThread.messages.length })}
                    </div>
                  </div>
                  <div className="mb-detail-body">
                    {selectedThread.messages.map((msg, idx) => {
                      const isReply = idx > 0;
                      const isUnread = unreadIncomingIds.has(msg.email_log_id);
                      const isNew = isUnread && (Date.now() - new Date(msg.created_at).getTime()) <= ONE_DAY_MS;
                      const linkedLabel = msg.student_id
                        ? `${t('mailbox.studentPrefix')}: ${msg.student_ref || msg.student_id} ${(msg.first_name || '')} ${(msg.last_name || '')}`.trim()
                        : msg.lead_id
                          ? `${t('mailbox.leadPrefix')}: ${msg.lead_ref || msg.lead_id} ${msg.lead_name || ''}`.trim()
                          : null;

                      return (
                        <div
                          key={msg.email_log_id}
                          className={`mb-msg${isReply ? ' mb-msg-reply' : ''}`}
                          style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                          {/* Card header */}
                          <div className={`mb-msg-header ${isReply ? 'mb-msg-header-reply' : 'mb-msg-header-original'}`}>
                            <div className={`mb-msg-type ${isReply ? 'mb-msg-type-reply' : 'mb-msg-type-original'}`}>
                              {isReply
                                ? <><FiCornerUpRight size={13} /> {t('mailbox.reply')} {idx}</>
                                : <><FiMail size={13} /> {t('mailbox.originalEmail')}</>
                              }
                            </div>
                            <span className="mb-msg-time-wrap">
                              {isNew && <span className="mb-badge mb-badge-new">{t('mailbox.newBadge')}</span>}
                              {isUnread && <span className="mb-badge mb-badge-unread">{t('mailbox.unreadBadge')}</span>}
                              <span className="mb-msg-time">{formatRelative(msg.created_at, t)}</span>
                            </span>
                          </div>

                          {/* Card body */}
                          <div className="mb-msg-body">
                            <div className="mb-msg-from-row">
                              <div className="mb-msg-sender">
                                <div className={`mb-avatar${isReply ? ' mb-avatar-green' : ''}`}>
                                  {getInitials(msg.sender_email)}
                                </div>
                                {msg.sender_email?.split('@')[0] || '-'}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--c-text-3)', fontVariantNumeric: 'tabular-nums' }}>
                                {formatDateTime(msg.created_at)}
                              </div>
                            </div>
                            <div className="mb-msg-addr">
                              <strong>{t('mailbox.fromField')}:</strong> {msg.sender_email} &nbsp;·&nbsp; <strong>{t('mailbox.toField')}:</strong> {msg.recipient_email}
                            </div>
                            <div className="mb-msg-content">
                              <MessageBodyFrame
                                html={msg.body_html}
                                emptyText={t('mailbox.noContent')}
                              />
                            </div>
                            <EmailLogAttachments
                              emailLogId={msg.email_log_id}
                              graphMessageId={msg.graph_message_id}
                              metadata={msg.metadata}
                            />
                          </div>

                          {/* Card footer */}
                          <div className="mb-msg-footer">
                            <div className="mb-msg-footer-item">
                              <FiInbox size={12} />
                              <span>{msg.mailbox_name ? `${msg.mailbox_name} (${msg.mailbox_email})` : '-'}</span>
                            </div>
                            {isAdminView && (
                              <div className="mb-msg-footer-item">
                                <FiUser size={12} />
                                <span>{msg.sent_by_name || '-'}</span>
                              </div>
                            )}
                            <div className="mb-msg-footer-item">
                              <FiCornerUpRight size={12} />
                              {linkedLabel ? (
                                <button
                                  type="button"
                                  className="mb-link"
                                  onClick={() => navigate(msg.student_id ? `/students/${msg.student_id}` : `/b2b-agencies/${msg.lead_id}`)}
                                >
                                  {linkedLabel}
                                </button>
                              ) : <span>{t('mailbox.notLinked')}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply section */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--c-border)', background: 'var(--c-bg-2, #faf9f6)' }}>
                    {!canReplyToSelected ? (
                      <div style={{ fontSize: 13, color: 'var(--c-text-3)', fontStyle: 'italic' }}>
                        {t('mailbox.noReplyPermission')}
                      </div>
                    ) : !canSendByFrenchSchedule ? (
                      <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
                        <strong>{t('mailbox.sendHours')}</strong>
                        <div style={{ marginTop: 4 }}>{sendWindowStatus?.message}</div>
                        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>{sendWindowStatus?.schedule_label}</div>
                      </div>
                    ) : !showReplyForm ? (
                      <button
                        type="button"
                        className="mb-btn mb-btn-primary mb-btn-sm"
                        onClick={() => { setShowReplyForm(true); setReplyText(''); setReplyFiles([]); }}
                      >
                        <FiCornerUpLeft size={13} /> {t('mailbox.reply')}
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-2)' }}>
                            <FiCornerUpLeft size={12} /> {t('mailbox.replyTo', { email: replyPreview.toEmail || selectedThread.latest.sender_email })}
                          </span>
                          <button type="button" className="mb-btn mb-btn-ghost mb-btn-sm" onClick={() => { setShowReplyForm(false); setReplyFiles([]); }}>
                            <FiX size={13} /> {t('common.cancel')}
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase' }}>{t('mailbox.fromField')}</div>
                          <div style={{ fontSize: 13, color: 'var(--c-text)', wordBreak: 'break-word' }}>
                            {replyPreview.mailboxEmail || '-'}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase' }}>{t('mailbox.toField')}</div>
                          <div style={{ fontSize: 13, color: 'var(--c-text)', wordBreak: 'break-word' }}>
                            {replyPreview.toEmail || '-'}
                          </div>
                        </div>
                        <EmailBodyEditor
                          themeMailbox
                          compact
                          value={replyText}
                          onChange={setReplyText}
                          placeholder={t('mailbox.replyPlaceholder')}
                          minHeight={160}
                        />
                        {userSignature && (
                          <div style={{ padding: '8px 12px', background: '#f9fafb', border: '1px dashed var(--c-border)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', marginBottom: 4 }}>{t('common.signature')}</div>
                            <div dangerouslySetInnerHTML={{ __html: userSignature }} />
                          </div>
                        )}
                        {replyFiles.length > 0 && (
                          <div className="mb-attach-list">
                            {replyFiles.map((f, i) => (
                              <div key={`${f.name}-${i}`} className="mb-attach-chip">
                                <FiFile size={12} />
                                <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                <button type="button" className="mb-attach-chip-remove" onClick={() => setReplyFiles(prev => prev.filter((_, j) => j !== i))}>
                                  <FiX size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input ref={replyFileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => setReplyFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
                            <button type="button" className="mb-btn mb-btn-ghost mb-btn-sm" onClick={() => replyFileRef.current?.click()}>
                              <FiPaperclip size={13} /> {t('mailbox.attach')}
                            </button>
                          </div>
                          <button type="button" className="mb-btn mb-btn-primary mb-btn-sm" onClick={handleReplyEmail} disabled={replying || !canSendByFrenchSchedule}>
                            <FiSend size={13} /> {replying ? t('mailbox.sendingBtn') : t('mailbox.sendReply')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="mb-empty">
                  <div className="mb-empty-icon"><FiMail size={24} style={{ color: 'var(--c-text-3)' }} /></div>
                  <span style={{ fontWeight: 600 }}>{t('mailbox.selectThread')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Status bar ── */}
      <div className="mb-statusbar">
        <FiMail size={13} />
        <span>{t('mailbox.totalEmails', { count: total })}</span>
        <div className="mb-statusbar-dot" />
        <span className={`mb-statusbar-badge ${isAdminView ? 'mb-badge-blue' : 'mb-badge-gray'}`} style={{ background: isAdminView ? 'var(--c-accent-soft)' : '#f0ede8', color: isAdminView ? 'var(--c-accent)' : 'var(--c-text-2)' }}>
          {isAdminView ? t('mailbox.adminView') : t('mailbox.myEmailsOnly')}
        </span>
        <div className="mb-statusbar-dot" />
        <span>{t('mailbox.crmThreadsOnly')}</span>
      </div>
    </div>
  );
};

export default MailboxList;